import React, { ReactNode, useState, useCallback } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations/index.ts';

import { TogglerIcon } from './toggler-icon.tsx';

export interface AccordionProps {
	header: string;
	content: ReactNode | JSX.Element;
}

export const Accordion = (props: AccordionProps) => {
	const { header, content } = props;
	const [isOpen, setIsOpen] = useState(false);

	const handleToggle = useCallback((event: React.SyntheticEvent<HTMLDetailsElement>) => {
		setIsOpen(event.currentTarget.open);
	}, []);

	return (
		<details css={containerCss} onToggle={handleToggle}>
			<summary css={headerContainerCss}>
				<h3 css={headerCss} className="card-title-sm">
					{header}
				</h3>
				<TogglerIcon icon={isOpen ? 'minus' : 'plus'} />
			</summary>

			<div css={contentCss}>{content}</div>
		</details>
	);
};

const containerCss = css({
	width: '100%',
	borderBlockEnd: `1px solid ${wacToken('color.neutral.light300')}`,
	interpolateSize: 'allow-keywords',
	padding: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover h3': {
		color: wacToken('color.extended.blue800'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&::details-content': {
		// the details-content psuedo element gets automatically applied to the content of the details tag (aka content under the summary tag)
		display: 'block',
		blockSize: 0,
		overflowY: 'clip',
		transitionProperty: 'content-visibility, block-size, display',
		transitionDuration: '.25s',
		transitionBehavior: 'allow-discrete',
		transitionTimingFunction: 'ease-in-out',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&[open]::details-content': {
		// the "open" property automatically gets added to the details element when opened
		blockSize: 'auto',
		'@starting-style': {
			blockSize: 0,
		},
	},
});

const headerContainerCss = css({
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'start',
	justifyContent: 'space-between',
	gap: token('space.200'),
});

const headerCss = css({
	margin: 0,
});

const contentCss = css({
	paddingBlockStart: token('space.200'),
});
